import React, { useState, useMemo } from "react";
import { useQuery, useLazyQuery, useApolloClient } from "@apollo/client";
import {
  Row,
  Col,
  Typography,
  Table,
  Button,
  Input,
  Card,
  Select,
  Empty,
} from "antd";
import {
  FileExcelFilled,
  SearchOutlined,
} from "@ant-design/icons";
import withClinic from "../../hooks/with_clinic";
import HorizontalScrollContainer from "../../components/horizontal_scroll_container";
import {
  ApolloResult,
  Clinic,
} from "../../store";
import {
  get_customer_by_salesperson,
  getCustomerBySalesPersonVar,
} from "../../graphql/order";
import {
  get_sellers,
  getSellersVar,
} from "../../graphql/user";
import LoadingSpinner from "../../components/loading_indicator";
import Error from "../../components/apollo_error";
import { formatMoney, getCurrency, formatPhoneNumber } from "../../helpers/utils";
import { PaginationConfig } from "antd/lib/pagination";
import * as XLSX from "xlsx";
import FileSaver from "file-saver";

interface P {
  currentClinic?: Clinic;
}

interface CustomerData {
  name: string;
  phoneNumber: string;
  memberId: string;
  totalSpend: number;
  lastInvoiceNumber: string;
  lastPurchaseDate: string;
  key?: string;
}

interface CustomerBySalesPersonResponse {
  data: CustomerData[];
  totalCount: number;
}

const CustomersBySalesPersonPage = (props: P) => {
  const clinicCode = props.currentClinic?.code || "";
  const clinicId = props.currentClinic?.id || "";
  const client = useApolloClient();
  const [selectedSellerId, setSelectedSellerId] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationConfig | null>({
    current: 1,
    pageSize: 20,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "30", "50", "100"],
  });
  const [exporting, setExporting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const take = pagination?.pageSize || 20;
  const skip = ((pagination?.current || 1) - 1) * take;

  // Fetch sellers
  const { data: sellersData, loading: sellersLoading } = useQuery<
    ApolloResult<"users", Array<{ id: string; name: string; display_name: string | null }>>
  >(get_sellers, {
    variables: getSellersVar(clinicId),
    skip: !clinicId,
    fetchPolicy: "cache-and-network",
  });

  // Fetch customers by salesperson (lazy query)
  const [getCustomers, { loading: customersLoading, data: customersData, error }] = useLazyQuery<
    ApolloResult<"getCustomerBySalesPerson", CustomerBySalesPersonResponse>
  >(get_customer_by_salesperson, {
    fetchPolicy: "network-only",
  });

  // Automatically fetch customers when seller is selected or pagination changes
  React.useEffect(() => {
    if (selectedSellerId && clinicCode) {
      getCustomers({
        variables: getCustomerBySalesPersonVar({
          clinicCode,
          sellerId: selectedSellerId,
          skip,
          take,
        }),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skip, take, selectedSellerId, clinicCode]);

  // Filter customers based on search term
  const filteredCustomers = useMemo(() => {
    if (!customersData?.getCustomerBySalesPerson?.data) return [];

    let filtered = customersData.getCustomerBySalesPerson.data;

    // Apply search filter
    if (searchTerm.trim() !== "") {
      const normalizedSearchTerm = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(
        (customer: CustomerData) =>
          (customer.name?.toLowerCase().includes(normalizedSearchTerm) || false) ||
          (customer.phoneNumber?.toLowerCase().includes(normalizedSearchTerm) || false) ||
          (customer.memberId?.toLowerCase().includes(normalizedSearchTerm) || false)
      );
    }

    return filtered.map((customer: CustomerData, index: number) => ({
      ...customer,
      key: `${customer.name}-${customer.phoneNumber}-${index}`,
    }));
  }, [customersData?.getCustomerBySalesPerson?.data, searchTerm]);

  // Get selected seller name
  const selectedSellerName = useMemo(() => {
    if (!selectedSellerId || !sellersData?.users) return "";
    const seller = sellersData.users.find((s) => s.id === selectedSellerId);
    return seller?.display_name || seller?.name || "";
  }, [selectedSellerId, sellersData?.users]);

  // Export to Excel
  const exportToExcel = async () => {
    if (!clinicCode || !selectedSellerId) return;
    setExporting(true);

    const fileType =
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
    const fileExtension = ".xlsx";

    try {
      // Fetch all customers for export (not paginated)
      const exportResult = await client.query<
        ApolloResult<"getCustomerBySalesPerson", CustomerBySalesPersonResponse>
      >({
        query: get_customer_by_salesperson,
        variables: getCustomerBySalesPersonVar({
          clinicCode,
          sellerId: selectedSellerId,
          skip: 0,
          take: 100000, // Large number to get all records
        }),
        fetchPolicy: "network-only",
      });

      const allCustomers = exportResult.data?.getCustomerBySalesPerson?.data || [];

      if (allCustomers.length === 0) {
        setExporting(false);
        return;
      }

      // Apply search filter if there's a search term
      let customersToExport = allCustomers;
      if (searchTerm.trim() !== "") {
        const normalizedSearchTerm = searchTerm.toLowerCase().trim();
        customersToExport = allCustomers.filter(
          (customer: CustomerData) =>
            (customer.name?.toLowerCase().includes(normalizedSearchTerm) || false) ||
            (customer.phoneNumber?.toLowerCase().includes(normalizedSearchTerm) || false) ||
            (customer.memberId?.toLowerCase().includes(normalizedSearchTerm) || false)
        );
      }

      const rows = customersToExport.map((customer: CustomerData) => ({
        "Customer Name": customer.name,
        "Phone Number": formatPhoneNumber(customer.phoneNumber, props.currentClinic) || "N/A",
        "Member ID": (customer.memberId && customer.memberId !== "N/A") ? customer.memberId : "",
        "Total Amount Spent": `${formatMoney(customer.totalSpend)} ${getCurrency(
          props.currentClinic
        )}`,
        "Last Purchase Date": customer.lastPurchaseDate,
        "Last Invoice Number": customer.lastInvoiceNumber,
      }));

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Customers");

      const fileName = `customers_by_salesperson_${selectedSellerName}_${new Date().toISOString().split('T')[0]}`;

      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], { type: fileType });
      FileSaver.saveAs(blob, fileName + fileExtension);
    } catch (error) {
      console.error("Export error:", error);
    } finally {
      setExporting(false);
    }
  };

  // Table columns
  const columns = [
    {
      title: "Customer Name",
      dataIndex: "name",
      key: "name",
      width: 200,
      ellipsis: true,
      sorter: (a: CustomerData, b: CustomerData) =>
        a.name.localeCompare(b.name),
    },
    {
      title: "Phone Number",
      dataIndex: "phoneNumber",
      key: "phoneNumber",
      width: 150,
      ellipsis: true,
      render: (v: string) => formatPhoneNumber(v, props.currentClinic) || "-",
    },
    {
      title: "Member ID",
      dataIndex: "memberId",
      key: "memberId",
      width: 120,
      ellipsis: true,
      render: (v: string) => (v && v !== "N/A" ? v : ""),
    },
    {
      title: "Total Amount Spent",
      dataIndex: "totalSpend",
      key: "totalSpend",
      width: 180,
      align: "right" as const,
      sorter: (a: CustomerData, b: CustomerData) => a.totalSpend - b.totalSpend,
      render: (v: number) =>
        `${formatMoney(v)} ${getCurrency(props.currentClinic)}`,
    },
    {
      title: "Last Purchase Date",
      dataIndex: "lastPurchaseDate",
      key: "lastPurchaseDate",
      width: 150,
      sorter: (a: CustomerData, b: CustomerData) =>
        new Date(a.lastPurchaseDate).getTime() - new Date(b.lastPurchaseDate).getTime(),
    },
    {
      title: "Last Invoice Number",
      dataIndex: "lastInvoiceNumber",
      key: "lastInvoiceNumber",
      width: 150,
      ellipsis: true,
    },
  ];

  // Control section with title
  const control = (
    <Row style={{ marginTop: -18, marginBottom: 16 }}>
      <Col flex={4}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          Customers by Salesperson
        </Typography.Title>
      </Col>
    </Row>
  );

  // Salesperson selection section
  const salespersonSection = (
    <Card title="Select Salesperson" style={{ marginBottom: 16 }}>
      <Select
        placeholder="Select Salesperson"
        style={{ width: "100%" }}
        value={selectedSellerId}
        onChange={(value: string | null) => {
          setSelectedSellerId(value);
          setPagination({ ...pagination, current: 1 });
        }}
        loading={sellersLoading}
        showSearch
        filterOption={(input: string, option: any) =>
          (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
        }
        options={(sellersData?.users || []).map((seller) => ({
          value: seller.id,
          label: seller.display_name || seller.name,
        }))}
      />
    </Card>
  );

  // Search and export section
  const filters = (
    <Row style={{ marginBottom: 16 }}>
      <Col flex="auto">
        <Input
          placeholder="Search by name, phone, or member ID"
          prefix={<SearchOutlined />}
          value={searchTerm}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setSearchTerm(e.target.value)
          }
          allowClear
        />
      </Col>
      <Col style={{ marginLeft: 8 }}>
        <Button
          size="small"
          loading={exporting}
          icon={<FileExcelFilled />}
          onClick={exportToExcel}
          disabled={!customersData || filteredCustomers.length === 0}
        >
          EXPORT TO EX
        </Button>
      </Col>
    </Row>
  );

  // Customers table
  const customersTable = (
    <Card>
      {customersLoading && !customersData ? (
        <LoadingSpinner />
      ) : error ? (
        <Error error={error} />
      ) : !selectedSellerId ? (
        <Empty description="Select a salesperson to view customers" />
      ) : filteredCustomers.length === 0 ? (
        <Empty description="No customers found" />
      ) : (
        <>
          <Typography.Text type="secondary" style={{ marginBottom: 16, display: "block" }}>
            Showing {filteredCustomers.length} customer{filteredCustomers.length !== 1 ? "s" : ""} for {selectedSellerName}
          </Typography.Text>
          <HorizontalScrollContainer>
            <Table
              dataSource={filteredCustomers}
              columns={columns}
              scroll={{ x: 1000 }}
              pagination={{
                total: customersData?.getCustomerBySalesPerson?.totalCount || filteredCustomers.length,
                current: pagination?.current || 1,
                pageSize: pagination?.pageSize || 20,
                defaultPageSize: 20,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "30", "50", "100"],
                onChange: (page: number, pageSize: number) => {
                  setPagination({
                    ...pagination,
                    current: page,
                    pageSize,
                  });
                },
                onShowSizeChange: (current: number, size: number) => {
                  setPagination({
                    ...pagination,
                    current: 1,
                    pageSize: size,
                  });
                },
              }}
              loading={customersLoading}
            />
          </HorizontalScrollContainer>
        </>
      )}
    </Card>
  );

  if (!clinicCode) {
    return (
      <Card>
        <Typography.Text type="secondary">
          Please select a clinic first to view customer data.
        </Typography.Text>
      </Card>
    );
  }

  return (
    <React.Fragment>
      {control}
      {salespersonSection}
      {filters}
      {customersTable}
    </React.Fragment>
  );
};

export default withClinic(CustomersBySalesPersonPage);

